import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import './style.scss';
import { Tooltip } from 'react-tooltip';
import { createPortal } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { copyText, requestIdleCallbackWithPollyfill } from 'common/utils/utils.js';
import toast from 'common/utils/toast';

import Icon from 'common/components/elements/icons';
import cx from 'classnames'

const TruncateText = ({
   text,
   className = '',
   textClass = '',
   textSize = '16px',
   fontWeight = '400',
   width = '100%',
   href,
   isLink,
   tooltipIdForHtmlContent,
   target = '',
   notShowTooltip = false,
   toastText,
   alwaysShowTooltip = false,
   tooltipId,
   onClick,
   highlitedText,
   isTextReactNode = false,
   hideCopyButton = false,
   showTootltipText,
   tooltipText = '',
   styles = {},
   innerTextAsHTML,
   customText,
   customIcon,
   customIconClassName,
   onClickCustomIcon,
   showCustomTextOnTooltip,
   handlerClick,
   hasClickInItemName = false,
   onMouseEnter = () => {},
   onMouseLeave = () => {},
   withoutTruncate = false,
   customIconProps = {},
   hideNBSP = false,
}) => {
   const ref = useRef(null);
   const textRef = useRef(null)

   const [showTootltip, setShowTooltip] = useState(false);
   useEffect(() => {
      window.addEventListener('resize', onMouseOver)
      return () => {
         window.removeEventListener('resize', onMouseOver)
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   useLayoutEffect(() => {
      onMouseOver();
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [text, textSize])

   const onMouseOver = e => {
      if(notShowTooltip) {
         return;
      }
      if(alwaysShowTooltip) {
         setShowTooltip(true)
         return
      }

      if(!ref.current) {
         return;
      }

      if(isTextReactNode) {
         const spanElement = document.createElement('span');
         spanElement.classList.add('absolute', 'whitespace-nowrap', 'invisible', 'top-0', 'left-0', 'h-full', 'span_to_calculate')
         spanElement.style.fontSize = textSize;
         spanElement.style.fontWeight = fontWeight;
         const spansList = ref.current.querySelectorAll('.span_to_calculate')
         if(spansList.length > 0) {
            spansList.forEach(el => {
               el.remove()
            })
         }

         // Append span to DOM before measuring
         ref.current.appendChild(spanElement);

         // Create root and render text
         const root = createRoot(spanElement);
         root.render(text);

         // Measure width and cleanup
         const callback = () => {
            if(spanElement && ref.current) {
               if(spanElement.offsetWidth > ref.current.offsetWidth) {
                  setShowTooltip(true);
               } else {
                  setShowTooltip(false);
               }
               root.unmount();
               if(ref.current.contains(spanElement)) {
                  ref.current.removeChild(spanElement);
               }
            }
         }
         if(typeof queueMicrotask === 'function') {
            queueMicrotask(callback);
         } else {
            setTimeout(callback, 0);
         }
      } else {
         if(!textRef.current) {
            return;
         }

         requestIdleCallbackWithPollyfill(() => {
            if(!textRef.current || !ref.current) {
               return;
            }
            if(textRef.current.scrollWidth > ref.current.offsetWidth) {
               setShowTooltip(true)
            } else {
               setShowTooltip(false)
            }
         })
      }
   }

   const onClickCopy = (e) => {
      copyText(text);
      if(toastText){
         toast.success(toastText);
         return;
      }
      let cutText = text;
      if(text.length > 25) {
         cutText = `${ text.slice(0, 25) }...`;
      }
      toast.success(`${ cutText } has been copied`);
   }

   const onClickIcon = (e) => {
      onClickCustomIcon(e)
   }

   if(tooltipIdForHtmlContent){
      return (
         <>
            <TruncateTextComponent
               showTootltip={ showTootltip }
               width={ width }
               text={ text }
               className={ className }
               contentref={ ref }
               isLink={ isLink }
               textClass={ textClass }
               href={ href }
               id={ `ams-html-tooltip-${ tooltipIdForHtmlContent }` }
               hasChildren
               target={ target }
               notShowTooltip={ notShowTooltip }
               onClick={ onClick }
               tooltipText={ tooltipText }
               styles={ styles }
               innerTextAsHTML={ innerTextAsHTML }
               withoutTruncate={ withoutTruncate }
               textRef={ textRef }
            />
            {
               createPortal(
                  <Tooltip
                     place='top'
                     className={ cx({
                        'ams-black-tooltips': true,
                     }) }
                     id={ showTootltip  ? `ams-html-tooltip-${ tooltipIdForHtmlContent }` : null }
                     clickable
                  >
                     <span
                        onClick={ e => {
                           e.preventDefault()
                           e.stopPropagation()
                        } }
                        role='presentation'
                     >
                        {
                           !customText && text
                        }
                        {
                           !hideNBSP && <>
                                 &nbsp;
                           </>
                        }
                        {
                           !!customText && customText
                        }
                        {
                           !hideNBSP && <>
                                 &nbsp;
                           </>
                        }
                        {
                           !hideCopyButton && !customIcon && (
                              <Icon
                                 name='copy'
                                 size='sm'
                                 className='cursor-pointer opacity-1 dark:text-major'
                                 color='major-dark'
                                 darkColor='major'
                                 onClick={ onClickCopy }
                              />
                           )
                        }
                        {
                           customIcon &&
                              <Icon
                                 name={ customIcon }
                                 size='sm'
                                 className={ `cursor-pointer opacity-1 dark:text-major ${ !!customIconClassName ? customIconClassName : '' }` }
                                 color='major-dark'
                                 darkColor='major'
                                 onClick={ onClickIcon }
                                 { ...customIconProps }
                              />
                        }
                     </span>
                  </Tooltip>
                  ,
                  document?.getElementById?.('tooltips')
               )
            }
         </>
      )
   }
   return (
      <TruncateTextComponent
         showTootltip={ showTootltip }
         width={ width }
         text={ text }
         className={ className }
         contentref={ ref }
         isLink={ isLink }
         textClass={ textClass }
         href={ href }
         target={ target }
         notShowTooltip={ notShowTooltip }
         id={ tooltipId }
         onClick={ onClick }
         highlitedText={ highlitedText }
         showTootltipText={ showTootltipText }
         tooltipText={ tooltipText }
         styles={ styles }
         innerTextAsHTML={ innerTextAsHTML }
         customText={ customText }
         showCustomTextOnTooltip={ showCustomTextOnTooltip }
         handlerClick={ handlerClick }
         hasClickInItemName={ hasClickInItemName }
         onMouseEnter={ onMouseEnter }
         onMouseLeave={ onMouseLeave }
         withoutTruncate={ withoutTruncate }
         textRef={ textRef }
      />

   );
};

const TruncateTextComponent = ({
   showTootltip,
   showTootltipText,
   width,
   text,
   className,
   contentref,
   isLink,
   textClass,
   href,
   id = 'ams-top-tooltip-clickable',
   hasChildren,
   target,
   notShowTooltip,
   onClick,
   highlitedText,
   tooltipText,
   styles,
   innerTextAsHTML,
   customText,
   showCustomTextOnTooltip,
   handlerClick,
   hasClickInItemName,
   onMouseEnter,
   onMouseLeave,
   withoutTruncate = false,
   textRef,
}) => {

   const getParts = () => {
      if(!text) return []
      return text.split(new RegExp(`(${ highlitedText })`, 'gi'));
   }

   let tooltipProps = {};

   if(!hasChildren && showTootltip && !notShowTooltip) {
      const tooltipText = showCustomTextOnTooltip ? customText : text;

      if(innerTextAsHTML) {
         tooltipProps = { 'data-tooltip-html': tooltipText };
      } else {
         tooltipProps = { 'data-tooltip-content': tooltipText };
      }
   } else {
      tooltipProps = {};
   }

   return (
      <div
         { ...tooltipProps }
         // data-tooltip-content={ !hasChildren && showTootltip && !notShowTooltip ? (showCustomTextOnTooltip ? customText : text) : '' }
         // data-tooltip-html={ !hasChildren && showTootltip && !notShowTooltip ? (showCustomTextOnTooltip ? customText : text) : '' }
         data-tooltip-id={ id ? id : null }
         style={ {
            maxWidth: width,
            // direction: 'rtl',
         } }
         ref={ contentref }
         className={ `${ className } truncate-text-content relative` }
      >
         {
            innerTextAsHTML
               ?
               (
                  <span
                     style={ styles }
                     role='presentation'
                     onMouseEnter={ () => {
                        if(onMouseEnter) onMouseEnter()
                     } }
                     onMouseLeave={ () => {
                        if(onMouseLeave) onMouseLeave()
                     } }
                     onClick={ hasClickInItemName ? handlerClick : onClick }
                     className={ cx({
                        'truncate': !withoutTruncate,
                        [textClass]: textClass,
                     }) }
                     ref={ textRef }
                     dangerouslySetInnerHTML={ { __html: text } } ></span>
               )
               :
               (
                  <>
                     {
                        !isLink && (
                           !highlitedText ? (
                              <span
                                 style={ styles }
                                 role='presentation'
                                 onClick={ hasClickInItemName ? handlerClick : onClick }
                                 onMouseEnter={ () => {
                                    if(onMouseEnter) onMouseEnter()
                                 } }
                                 onMouseLeave={ () => {
                                    if(onMouseLeave) onMouseLeave()
                                 } }
                                 className={ cx({
                                    'truncate': !withoutTruncate,
                                    [textClass]: textClass,
                                 }) }
                                 ref={ textRef }
                              >
                                 {text}
                              </span>
                           ) : (
                              <span
                                 role='presentation'
                                 onClick={ hasClickInItemName ? handlerClick : onClick }
                                 onMouseEnter={ () => {
                                    if(onMouseEnter) onMouseEnter()
                                 } }
                                 onMouseLeave={ () => {
                                    if(onMouseLeave) onMouseLeave()
                                 } }
                                 className={ cx({
                                    'truncate': !withoutTruncate,
                                    [textClass]: textClass,
                                 }) }
                                 style={ styles }
                                 ref={ textRef }
                              >
                                 {getParts().map((part, index) => (
                                    part.toLowerCase() === highlitedText.toLowerCase() ? (
                                       <span key={ index } className='_highlighted_text bg-pending'>{part}</span>
                                    ) : (
                                       <span key={ index } className={ textClass }>{part}</span>
                                    )
                                 ))}
                              </span>
                           )
                        )
                     }

                     {
                        isLink && (
                           <a
                              onClick={ hasClickInItemName ? handlerClick : onClick }
                              onMouseEnter={ () => {
                                 if(onMouseEnter) onMouseEnter()
                              } }
                              onMouseLeave={ () => {
                                 if(onMouseLeave) onMouseLeave()
                              } }
                              href={ href }
                              className={ cx({
                                 'truncate': !withoutTruncate,
                                 [textClass]: textClass,
                              }) }
                              ref={ textRef }
                              target={ target || '' }> {text} </a>
                        )
                     }
                  </>

               )
         }
      </div>
   )
}


TruncateText.propTypes = {
   text: PropTypes.string,
   className: PropTypes.string,
   textClass: PropTypes.string,
   textSize: PropTypes.string,
   fontWeight: PropTypes.string,
   width: PropTypes.string,
   href: PropTypes.string,
   isLink: PropTypes.bool,
   tooltipIdForHtmlContent: PropTypes.string,
   target: PropTypes.string,
   notShowTooltip: PropTypes.bool,
   // newStyle: PropTypes.bool,
   toastText: PropTypes.string,
   alwaysShowTooltip: PropTypes.bool,
   tooltipId: PropTypes.string,
   onClick: PropTypes.func,
   highlitedText: PropTypes.string,
   showTootltipText: PropTypes.string,
   isTextReactNode: PropTypes.bool,
   hideCopyButton: PropTypes.bool,
   tooltipText: PropTypes.string,
   styles: PropTypes.object,
   innerTextAsHTML: PropTypes.bool,
   customText: PropTypes.string,
   customIcon: PropTypes.string,
   customIconClassName: PropTypes.string,
   onClickCustomIcon: PropTypes.func,
   showCustomTextOnTooltip: PropTypes.bool,
   handlerClick: PropTypes.func,
   hasClickInItemName: PropTypes.bool,
   onMouseEnter: PropTypes.func,
   onMouseLeave: PropTypes.func,
   withoutTruncate: PropTypes.bool,
   customIconProps: PropTypes.object,
   hideNBSP: PropTypes.bool,
};
TruncateTextComponent.propTypes = {
   text: PropTypes.string,
   className: PropTypes.string,
   textClass: PropTypes.string,
   width: PropTypes.string,
   href: PropTypes.string,
   isLink: PropTypes.bool,
   showTootltip: PropTypes.bool,
   hasChildren: PropTypes.bool,
   contentref: PropTypes.any,
   id: PropTypes.string,
   target: PropTypes.string,
   notShowTooltip: PropTypes.bool,
   onClick: PropTypes.func,
   highlitedText: PropTypes.string,
   showTootltipText: PropTypes.string,
   tooltipText: PropTypes.string,
   styles: PropTypes.object,
   innerTextAsHTML: PropTypes.bool,
   customText: PropTypes.string,
   showCustomTextOnTooltip: PropTypes.bool,
   handlerClick: PropTypes.func,
   hasClickInItemName: PropTypes.bool,
   onMouseEnter: PropTypes.func,
   onMouseLeave: PropTypes.func,
   withoutTruncate: PropTypes.bool,
   textRef: PropTypes.any,
};


export default TruncateText;
